"use client";

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../apis/authService";
import { GoogleLoginButton } from "../ui/google-login-button";

export function SignInForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshMe } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const fromQuery = params.get("sessionExpired") === "1";
    const fromState = Boolean(location.state?.sessionExpired);

    if (fromQuery || fromState) {
      setMessage({
        type: "error",
        text: "Your session has expired. Please sign in again.",
      });
      if (fromQuery) {
        params.delete("sessionExpired");
        const next = params.toString();
        navigate(
          { pathname: location.pathname, search: next ? `?${next}` : "" },
          { replace: true, state: {} }
        );
      } else {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, location.search, location.pathname, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await authService.loginInitiateOtp(
        formData.email,
        formData.password
      );

      if (result.success && result.otpBypassed) {
        await refreshMe();
        navigate(result.redirect || "/scribe", { replace: true });
        return;
      }

      if (result.success && result.totpRequired) {
        setMessage({
          type: "success",
          text: "Two-factor authentication required",
        });
        const rememberParam = formData.rememberMe ? "&remember=1" : "";
        setTimeout(() => {
          navigate(
            `/otp-verify?purpose=totp&userId=${result.userId}${rememberParam}`,
            { replace: true }
          );
        }, 1200);
        return;
      }

      if (result.success && result.otpRequired) {
        setMessage({
          type: "success",
          text: "OTP sent to your email",
        });
        const rememberParam = formData.rememberMe ? "&remember=1" : "";
        setTimeout(() => {
          navigate(
            `/otp-verify?purpose=login&userId=${result.userId}${rememberParam}`,
            { replace: true }
          );
        }, 1500);
      } else {
        setMessage({
          type: "error",
          text: result.message || "Login failed",
        });
      }
    } catch {
      setMessage({
        type: "error",
        text: "Login failed. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (user) => {
    await refreshMe();
    setMessage({ type: "success", text: `Welcome ${user.name}!` });
    setTimeout(() => navigate("/scribe"), 1500);
  };

  const handleGoogleError = (error) => {
    setMessage({ type: "error", text: error });
  };

  return (
    <div className="space-y-4">
      {/* Message box */}
      {message && (
        <div
          className={`p-3 rounded-lg flex items-center gap-2 ${message.type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
            }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      <GoogleLoginButton
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        disabled={isLoading}
      />

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-base text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Email field */}
        <div>
          <div className="relative flex items-center">
            <Mail size={20} className="absolute left-4 text-gray-400 pointer-events-none" />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              className="w-full h-12 pl-12 pr-4 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg text-slate-900 placeholder:text-gray-400"
            />
          </div>
          {errors.email && (
            <p className="text-sm text-red-500 mt-1.5">{errors.email}</p>
          )}
        </div>

        {/* Password field */}
        <div>
          <div className="relative flex items-center">
            <Lock size={20} className="absolute left-4 text-gray-400 pointer-events-none" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
              className="w-full h-12 pl-12 pr-12 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg text-slate-900 placeholder:text-gray-400"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center p-0"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-500 mt-1.5">{errors.password}</p>
          )}
        </div>

        {/* Remember me & Forgot password */}
        <div className="flex justify-between items-center text-base mb-5">
          <label className="flex items-center gap-2 text-gray-600 text-base">
            <input
              type="checkbox"
              checked={formData.rememberMe}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  rememberMe: e.target.checked,
                }))
              }
              className="w-4.5 h-4.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Remember me
          </label>
          <Link to="/forgot-password" className="text-blue-600 hover:underline text-base">
            Forgot password?
          </Link>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-[52px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white font-medium text-base transition disabled:opacity-70"
        >
          {isLoading ? "Signing In..." : "Sign In"}
        </button>
      </form>

      <p className="text-center text-gray-500 mt-5 text-base">
        Don't have an account?{" "}
        <Link to="/signup" className="text-blue-600 font-medium hover:underline text-base">
          Sign Up
        </Link>
      </p>
    </div>
  );
}